import isEmpty from 'lodash/isEmpty';
import { setLoading } from '../../index';
import {
  getLineItemsOfSelectedOrder,
  getOrderingSystem,
  getOrderModificationsAreAllowed,
  getSelectedLineItems,
} from '../../../selectors/tools';
import { extractSelectedTicketFromState, getDecisionNodeAtPath } from '../../../extractFromState';
import * as ordersActions from '../OrdersSearchActions';
import { showErrorPopup } from '../../../components/popups/ErrorPopup/ErrorPopup';
import { getItemName } from '../../../util';
import { ORDER_CANCEL_VALID_STATUSES, ORDER_SHIP_TO_COUNTRY } from '../../../constants/revolve';
import { replaceTemplatedTextForMultipleItems } from '../../../selectors/templating';
import { addEdgeToDecisionTreePath, addSnippetText } from '../../searchActions';
import { isLiveChat } from '../../../util/chat';

export const processRevolveOrderItemsCancelFlow = () => {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoading(true, 'searchPage'));
      const state = getState();
      const orderingSystem = getOrderingSystem(state);
      const userSelectedItems = getSelectedLineItems(state);
      const lineItemsOfSelectedOrder = getLineItemsOfSelectedOrder(state);
      const selectedItems = userSelectedItems.length ? userSelectedItems : lineItemsOfSelectedOrder;
      const decisionNode = getDecisionNodeAtPath(state);

      dispatch(ordersActions.clearSnippetText());
      dispatch(ordersActions.clearCancelRefundDiscountTreeItems());

      if ('revolve' !== orderingSystem) {
        return showErrorPopup(`${orderingSystem} is not supported`, 'Error');
      }

      if (!getOrderModificationsAreAllowed(state)) {
        return showErrorPopup(`${orderingSystem} order modifications are not allowed`, 'Error');
      }
      const validItemsSnippetData = [];
      const invalidItemsSnippetData = [];

      selectedItems.forEach((item) => {
        const itemSnippetData = {};
        itemSnippetData.itemName = getItemName(item, orderingSystem);
        if (ORDER_CANCEL_VALID_STATUSES.includes(item.status)) {
          dispatch(ordersActions.addCancelAndRefundItem(item));
          validItemsSnippetData.push(itemSnippetData);
        } else {
          invalidItemsSnippetData.push(itemSnippetData);
        }
      });

      if (validItemsSnippetData.length) {
        const validItemsSnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemValidSnippet,
          validItemsSnippetData
        );
        validItemsSnippet && dispatch(addSnippetText(validItemsSnippet));
      }

      const allItemsValidForCancel = validItemsSnippetData.length && !invalidItemsSnippetData.length;
      const partialItemsValidForCancel = validItemsSnippetData.length && invalidItemsSnippetData.length;
      const noValidItemsForCancel = !validItemsSnippetData.length && invalidItemsSnippetData.length;

      if (allItemsValidForCancel) {
        dispatch(addEdgeToDecisionTreePath('all_valid_items'));
      } else if (partialItemsValidForCancel) {
        dispatch(addEdgeToDecisionTreePath('partially_valid_items'));
      } else if (noValidItemsForCancel) {
        dispatch(addEdgeToDecisionTreePath('no_valid_items'));
      }
    } catch (e) {
      return showErrorPopup(`Failed processing order data`, 'Error');
    } finally {
      dispatch(setLoading(false, 'searchPage'));
    }
  };
};

export const processRevolveOrderShipToCountry = (choice) => {
  return async (dispatch, getState) => {
    dispatch(setLoading(true, 'searchPage'));
    const state = getState();
    const orderingSystem = getOrderingSystem(state);
    if ('revolve' !== orderingSystem) {
      return showErrorPopup(`${orderingSystem} is not supported`, 'Error');
    }
    const selectedTicket = extractSelectedTicketFromState(state);
    if (!selectedTicket) {
      return showErrorPopup('selectedTicket could not be found', 'Error');
    }
    const { communicationType } = selectedTicket;
    const ticketIsLiveChat = isLiveChat(communicationType);
    const userSelectedItems = getSelectedLineItems(state);
    const lineItemsOfSelectedOrder = getLineItemsOfSelectedOrder(state);
    const selectedItems = userSelectedItems.length ? userSelectedItems : lineItemsOfSelectedOrder;
    const decisionNode = getDecisionNodeAtPath(state);
    const invalidItemsSnippetData = [];
    selectedItems.forEach((item) => {
      const itemSnippetData = {
        itemName: getItemName(item, orderingSystem),
      };
      if (!ORDER_CANCEL_VALID_STATUSES.includes(item.status)) {
        invalidItemsSnippetData.push(itemSnippetData);
      }
    });
    if (isEmpty(invalidItemsSnippetData)) {
      return null;
    }
    if (ticketIsLiveChat) {
      switch (choice) {
        case ORDER_SHIP_TO_COUNTRY.INTERNATIONAL:
          const chatInternationalSnippet = replaceTemplatedTextForMultipleItems(
            decisionNode.chatInternationalSnippet,
            invalidItemsSnippetData
          );
          chatInternationalSnippet && dispatch(addSnippetText(chatInternationalSnippet));
          break;
        case ORDER_SHIP_TO_COUNTRY.DOMESTIC:
          const chatDomesticSnippet = replaceTemplatedTextForMultipleItems(
            decisionNode.chatDomesticSnippet,
            invalidItemsSnippetData
          );
          chatDomesticSnippet && dispatch(addSnippetText(chatDomesticSnippet));
          break;
        default:
          return null;
      }
    } else {
      switch (choice) {
        case ORDER_SHIP_TO_COUNTRY.INTERNATIONAL:
          const mailInternationalSnippet = replaceTemplatedTextForMultipleItems(
            decisionNode.mailInternationalSnippet,
            invalidItemsSnippetData
          );
          mailInternationalSnippet && dispatch(addSnippetText(mailInternationalSnippet));
          break;
        case ORDER_SHIP_TO_COUNTRY.DOMESTIC:
          const mailDomesticSnippet = replaceTemplatedTextForMultipleItems(
            decisionNode.mailDomesticSnippet,
            invalidItemsSnippetData
          );
          mailDomesticSnippet && dispatch(addSnippetText(mailDomesticSnippet));
          break;
        default:
          return null;
      }
    }
  };
};
